/**
 * BiometricCapture — captures one signature (Surface Pen on touch-screen)
 * and one fingerprint (SecuGen Hamster Pro 20 via the SecuGen WebAPI
 * Server running locally on the desk PC) and uploads both to our backend
 * (GridFS). Exposes `value` + `onChange` so it slots into existing
 * controlled form state on the Bike Handover / Takeover dialogs.
 *
 * Hardware expectations (one-time setup on the rental-desk PC):
 *   • Install SecuGen WebAPI Server (`SGIBioSrv` Windows service)
 *   • Plug in the HU20 USB scanner
 *   • The service listens on `https://localhost:8443` and `http://localhost:8000`
 *     We probe HTTPS first; if blocked by the browser cert, fall back to HTTP.
 *
 * No browser plugin is required — we use plain `fetch`. The bridge
 * returns `{ BMPBase64, ISOTemplateBase64, Quality, ErrorCode }`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Eraser, Fingerprint, Loader2, ShieldCheck, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('access_token') || ''}` },
});

// SecuGen WebAPI Server defaults — both endpoints are tried in order
const SECUGEN_ENDPOINTS = [
  'https://localhost:8443/SGIFPCapture',
  'http://localhost:8000/SGIFPCapture',
];
const SECUGEN_TIMEOUT_MS = 10000;
const SECUGEN_QUALITY_THRESHOLD = 50;   // 0-100 — anything below triggers a "low quality" warning

// ---------------------------------------------------------------------------
// SignaturePad (Pointer Events — Surface Pen friendly)
// ---------------------------------------------------------------------------

function SignaturePad({ value, onCapture, testid = 'signature' }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [dirty, setDirty] = useState(false);

  // Hydrate a previously captured image (e.g. after re-opening the form)
  useEffect(() => {
    if (!value || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const img = new Image();
    img.onload = () => { ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height); ctx.drawImage(img, 0, 0); };
    img.src = value;
  }, [value]);

  const point = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    canvasRef.current.setPointerCapture(e.pointerId);
    drawing.current = true;
    last.current = point(e);
  };
  const onMove = (e) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const p = point(e);
    // Surface Pen exposes a 0..1 pressure value; vary stroke width with it.
    const pressure = e.pressure && e.pressure > 0 ? e.pressure : 0.5;
    ctx.strokeStyle = '#111';
    ctx.lineCap = 'round';
    ctx.lineWidth = 1.0 + 2.5 * pressure;
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (!dirty) setDirty(true);
  };
  const onUp = (e) => {
    if (!drawing.current) return;
    drawing.current = false;
    try { canvasRef.current.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }
    // Push capture upward
    const data = canvasRef.current.toDataURL('image/png');
    onCapture(data);
  };

  const clear = useCallback(() => {
    const c = canvasRef.current; if (!c) return;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    setDirty(false);
    onCapture(null);
  }, [onCapture]);

  return (
    <div className="border border-slate-300 rounded bg-white">
      <canvas
        ref={canvasRef}
        width={420} height={150}
        className="block cursor-crosshair touch-none w-full"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        data-testid={`${testid}-canvas`}
      />
      <div className="flex items-center justify-between text-[10px] text-slate-500 px-2 py-1 border-t border-slate-200 bg-slate-50">
        <span>Sign with Surface Pen or finger</span>
        <button type="button" className="flex items-center gap-1 hover:text-slate-700" onClick={clear} data-testid={`${testid}-clear`}>
          <Eraser className="w-3 h-3" /> Clear
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FingerprintCapture (SecuGen WebAPI bridge)
// ---------------------------------------------------------------------------

async function callSecugen() {
  // Form-urlencoded body per SecuGen WebAPI spec
  const body = new URLSearchParams({
    Timeout: String(SECUGEN_TIMEOUT_MS),
    Quality: '50',
    licstr: '',
    templateFormat: 'ISO',
    imageWSQRate: '0.75',
  });
  let lastErr = null;
  for (const url of SECUGEN_ENDPOINTS) {
    try {
      const res = await fetch(url, { method: 'POST', body });
      if (!res.ok) { lastErr = new Error(`HTTP ${res.status}`); continue; }
      const data = await res.json();
      // SecuGen returns ErrorCode === 0 on success
      if (data.ErrorCode && data.ErrorCode !== 0) {
        lastErr = new Error(`SecuGen ErrorCode ${data.ErrorCode}: ${data.ErrorMessage || ''}`);
        continue;
      }
      return data;
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error('SecuGen WebAPI Server unreachable');
}

function FingerprintScanner({ value, onCapture, testid = 'fingerprint' }) {
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState(null);

  const capture = async () => {
    setError(null);
    setCapturing(true);
    try {
      const data = await callSecugen();
      // SecuGen returns base64 BMP in BMPBase64 (without data URL prefix)
      const bmp = data.BMPBase64 || data.bmpBase64;
      if (!bmp) throw new Error('No BMPBase64 returned by the scanner.');
      const template = data.TemplateBase64 || data.ISOTemplateBase64 || data.IsoTemplate || null;
      const quality = Number(data.NFIQ || data.Quality || 0);
      onCapture({
        image_base64: `data:image/bmp;base64,${bmp}`,
        template_base64: template || null,
        quality: quality || null,
        device_serial: data.SerialNumber || null,
        ok: true,
      });
    } catch (e) {
      const msg = (e?.message || String(e));
      setError(msg);
      onCapture(null);
    } finally {
      setCapturing(false);
    }
  };

  const clear = () => { onCapture(null); setError(null); };

  return (
    <div className="border border-slate-300 rounded bg-white p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <Fingerprint className="w-4 h-4 text-blue-600" /> Thumb Print
          <span className="text-red-500 text-sm">*</span>
          {value?.quality ? (
            <Badge variant="outline" className={value.quality >= SECUGEN_QUALITY_THRESHOLD ? 'bg-green-50 text-green-700 border-green-300' : 'bg-amber-50 text-amber-700 border-amber-300'}>
              Quality {value.quality}
            </Badge>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {value?.image_base64 && (
            <Button type="button" size="sm" variant="outline" onClick={clear} data-testid={`${testid}-clear`}>
              <Eraser className="w-3 h-3 mr-1" /> Clear
            </Button>
          )}
          <Button type="button" size="sm" onClick={capture} disabled={capturing} data-testid={`${testid}-capture`}>
            {capturing
              ? (<><Loader2 className="w-3 h-3 mr-1 animate-spin" />Place thumb…</>)
              : (<><Fingerprint className="w-3 h-3 mr-1" />{value?.image_base64 ? 'Re-capture' : 'Capture'}</>)}
          </Button>
        </div>
      </div>

      {/* Preview */}
      <div className="flex items-center justify-center bg-slate-50 border border-dashed border-slate-300 rounded h-40">
        {value?.image_base64 ? (
          <img
            src={value.image_base64}
            alt="Captured thumb print"
            className="h-36 object-contain"
            data-testid={`${testid}-preview`}
          />
        ) : (
          <div className="text-xs text-slate-400 text-center px-3">
            <p>Press <strong>Capture</strong> and place rider&apos;s thumb on the SecuGen HU20 scanner.</p>
            <p className="text-[10px] mt-1 text-slate-500">Optional — you can proceed without a scan.</p>
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="font-medium">Scanner not reachable.</p>
            <p>{error}</p>
            <p className="text-[10px] text-red-600">
              Make sure <strong>SecuGen WebAPI Server (SGIBioSrv)</strong> is running on this PC and the HU20 is plugged in.
              The browser must trust <code>https://localhost:8443</code> (one-time accept the self-signed cert).
            </p>
          </div>
        </div>
      )}
      {value?.image_base64 && !error && (
        <div className="flex items-center gap-1.5 text-[10px] text-green-700">
          <ShieldCheck className="w-3 h-3" /> Captured · ISO template stored on submit
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// BiometricCapture — orchestrator. Persists artefacts on `save()` and
// returns URLs back to the parent so it can attach them to its form.
// ---------------------------------------------------------------------------

/**
 * Props:
 *   value: { signature_url, fingerprint_url, fingerprint_template_url, fingerprint_quality, device_serial }
 *   onChange(value)
 *   formType: "handover" | "takeover"
 *   employeeId?: string
 *   riderName?: string
 *
 * The component holds the in-progress capture state internally; once the
 * user is happy and submits the parent form, the parent should call
 * `await ref.current.save()` to upload artefacts to the backend and
 * receive the GridFS URLs. We expose that imperatively to keep the
 * upload tied to the form submit (no orphan uploads on cancel).
 */
const BiometricCapture = ({ value, onChange, formType, employeeId, riderName }) => {
  const [signaturePng, setSignaturePng] = useState(null);
  const [fingerprint, setFingerprint] = useState(null); // { image_base64, template_base64, quality, device_serial }
  const uploadingRef = useRef(false);

  // Latest props/state mirror — used by the stable `upload` callback below
  // so it always reads the freshest values without changing identity.
  const stateRef = useRef({});
  stateRef.current = { signaturePng, fingerprint, value, formType, employeeId, riderName };

  // Parent's `onChange` is typically an inline function (new identity per
  // render). Keep a ref so our publish-effect doesn't depend on it.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Stable upload callback (identity never changes → no re-publish loop).
  const upload = useCallback(async () => {
    if (uploadingRef.current) return stateRef.current.value || {};
    uploadingRef.current = true;
    const s = stateRef.current;
    const out = { ...(s.value || {}) };
    try {
      if (s.signaturePng) {
        const res = await axios.post(`${API}/biometrics/capture`, {
          kind: 'signature',
          image_base64: s.signaturePng,
          form_type: s.formType,
          employee_id: s.employeeId,
          notes: s.riderName,
        }, getAuth());
        out.signature_url = res.data?.image_url;
        out.signature_captured_at = res.data?.captured_at;
      }
      if (s.fingerprint?.image_base64) {
        const res = await axios.post(`${API}/biometrics/capture`, {
          kind: 'fingerprint',
          image_base64: s.fingerprint.image_base64,
          template_base64: s.fingerprint.template_base64,
          quality: s.fingerprint.quality,
          device_serial: s.fingerprint.device_serial,
          form_type: s.formType,
          employee_id: s.employeeId,
          notes: s.riderName,
        }, getAuth());
        out.fingerprint_url = res.data?.image_url;
        out.fingerprint_template_url = res.data?.template_url;
        out.fingerprint_quality = s.fingerprint.quality || null;
        out.fingerprint_device_serial = s.fingerprint.device_serial || null;
        out.biometrics_captured_at = res.data?.captured_at;
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Biometric upload failed';
      toast.error(msg);
      throw e;
    } finally {
      uploadingRef.current = false;
    }
    return out;
  }, []);

  // Publish capture state + the stable upload callback to the parent.
  useEffect(() => {
    onChangeRef.current?.((prev) => ({
      ...(prev || {}),
      has_signature: !!signaturePng,
      has_fingerprint: !!fingerprint?.image_base64,
      _signature_b64: signaturePng,
      _fingerprint: fingerprint,
      _uploadBiometrics: upload,
    }));
  }, [signaturePng, fingerprint, upload]);

  return (
    <Card className="border-blue-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-blue-600" /> Rider Biometric Capture
        </CardTitle>
        <CardDescription className="text-[11px]">
          Digital signature on the Surface Pen is required. Thumb print on the SecuGen HU20 scanner is optional — capture it if the scanner is available.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1">
          <p className="text-[11px] uppercase tracking-wide text-slate-500">Signature</p>
          <SignaturePad value={signaturePng} onCapture={setSignaturePng} testid="biometric-signature" />
        </div>
        <div className="space-y-1">
          <p className="text-[11px] uppercase tracking-wide text-slate-500">Thumb Print <span className="text-slate-400 normal-case">(optional)</span></p>
          <FingerprintScanner value={fingerprint} onCapture={setFingerprint} testid="biometric-fingerprint" />
        </div>
      </CardContent>
    </Card>
  );
};

export default BiometricCapture;

// Convenience helper for parents: pull `_uploadBiometrics` from the form
// state and call it; returns the new partial value to merge into the
// submit payload. Keeps the API ergonomic when the form has many fields.
export async function uploadBiometrics(formStateValue) {
  if (!formStateValue?._uploadBiometrics) return formStateValue || {};
  const merged = await formStateValue._uploadBiometrics();
  // Strip transient _b64 fields before returning
  const { _signature_b64, _fingerprint, _uploadBiometrics, has_signature, has_fingerprint, ...clean } = merged || {};
  return clean;
}
